import { CtOrder } from '@/models/interfaces/content-type'
import { isUUID } from '.'
import PostalAddress from './postalAddress'
import { Address } from '@/models/generics'

export default class CustomerOrder {
  //
  /**
   * @description input validation. every validation function can also be used without any arguments, in which case it validates the current value of the validation target
                  Note that when calling a (validation-)function, "undefined" is still a valid argument, and the default argument value will not be used.
   */
  static validate (order: Partial<CtOrder>) {
    return {
      // validates the minimal requirement for an Order instance to be constructed
      preflight: () => !!((order?.id && typeof order?.id === 'string') && order?.catalogue),
      all: (): Map<string, true | false | Array<Record<string, boolean>>> => new Map(Object.entries(this.validate)
        .map(([key, fn]) =>
          [key, key === 'all' ? true : fn()])) as Map<string, true | false | Array<Record<string, boolean>>>,
      // validates that the referenced catalogue is a valid UUID (no existence check)
      catalogue: (uuid = order.catalogue) => {
        return isUUID(uuid)
      },
      // validates that each Packing instance at least has all the required values (XYZ & weight etc.)
      packing: (packing: CtOrder['packing'] = order.packing) => {
        if (!packing) return false
        if (!order.products) throw Error('Order packing validator missing order.products')

        const base = packing.length && packing.every(p =>
          p.dimensions?.x && p.dimensions?.y && p.dimensions?.z &&
          p.components?.length && p.products?.length && p.weight)
            ? true
            : !packing.length ? [{ length: false }] : packing.map(p => ({ data: false })) // NOTE just a generic data: false indication instead of detailed by-field indication

        if (base !== true) return base
        // check if every order-product is at least included in a package once
        const notInRegisteredPacking = order.products.filter(p => (order.packing?.findIndex(pa => pa.products.find(pp => pp.id === p.id)) ?? -1) < 0)

        packing.map(p => p?.components.map(c => `${c.id}: ${c.quantity}`))

        const packedQuantitiesByComponent = packing.reduce((dict,p) => {
          // this.log.fn('pack validate >packing').info(p.components)
          p.components.forEach(c => {
            if (!dict[c.id]) dict[c.id] = c.quantity
            else dict[c.id] = (dict[c.id] + c.quantity)
          })

          return dict
        }, {} as { [key:string]: number })

        // NOTE: product-component quantity reflects the quantity set by items of CtProduct.components (i.e the stock-item)
        // this is multiplied by the product quantity to get the total
        const productQuantitiesByComponent = order.products.reduce((dict,p) => {
          // this.log.fn('pack validate <product').info(p.quantity, p.components)
          p.components.forEach(c => {
            if (!dict[c.sys.id]) dict[c.sys.id] = (c.quantity || 1) * p.quantity
            else dict[c.sys.id] = dict[c.sys.id] + ((c.quantity || 1) * p.quantity)
          })

          return dict
        }, {} as { [key:string]: number })

        const quantitiesVerified = order.products.every(p => {
          return p.components.every(c => packedQuantitiesByComponent[c.sys.id] === productQuantitiesByComponent[c.sys.id])
        })

        if (process.env.LOG_ORDER_PACKING_VALIDATION) console.warn('validate.packing', `packedQuantitiesByComponent: ${JSON.stringify(packedQuantitiesByComponent)}`, `productQuantitiesByComponent: ${JSON.stringify(productQuantitiesByComponent)}`)
        if (!quantitiesVerified) {
          console.warn('validate.packing',
            'Detected invalid packing component values',
            `packedQuantitiesByComponent: ${JSON.stringify(packedQuantitiesByComponent)}`,
            `productQuantitiesByComponent: ${JSON.stringify(productQuantitiesByComponent)}`,
            'packing sums', packing.map(p => p?.components.map(c => `${c.id}: ${c.quantity}`)),
            'product sums', order.products.map(p => p?.components.map(c => `${c.sys.id}: ${c.quantity*p.quantity} (pq ${p.quantity} * cq ${c.quantity})`))
          )
        }

        if (notInRegisteredPacking?.length) console.warn('validate.packing', 'Detected product ids not registered in any packing', notInRegisteredPacking)
        if (notInRegisteredPacking?.length || !quantitiesVerified)
          return [{ product_coverage: !(notInRegisteredPacking?.length > 0), product_quantities: !quantitiesVerified }]

        return true
      },
      // validates that the address is at least minimally identifiable
      address: (address: PostalAddress | Address = order?.shipping_address || {}) => {
        if (!address) return false
        return address instanceof PostalAddress
          ? address.valid ? true : address.validation_state
          : PostalAddress.RequiredKeys.every(key => address[key]) ? true : Object.fromEntries(PostalAddress.RequiredKeys.map(key => [key, !!address[key]]))
      },
      shipments: (shipments: CtOrder['shipments'] = order.shipments) => {
        if (!shipments?.length) return true
        return shipments.every(s => s.id && Array.isArray(s.labels) && s.carrier) ? true : [{ data: false }]
      },
      fulfillments: (fulfillments: CtOrder['fulfillments'] = order.fulfillments, requireMatchingShipment: CtOrder['shipments'] | false = false) => {
        if (!fulfillments?.length) return true
        return fulfillments.every(f => f.service && f.shipments && (requireMatchingShipment === false || f.shipments.find(i => requireMatchingShipment?.find(s => s.id === i.id))))
          ? true
          : fulfillments.map(f => ({ data: false }))
      },
      // validates that each OrderProduct has at least a quantity and an identifier (sku/ean)
      products: (products: CtOrder['products'] = order.products || []) => {
        if (!products) return false
        return products.length && products.every(p => p.quantity > 0 &&
          !!p.sys?.id &&
          !!p.id &&
          !!p.status &&
          (p.sku || p.ean) &&
          p.components?.length > 0 &&
          p.components.every(c => !!c.sys.id && (c.sku || c.ean || c.title) && c.status) &&
          !!p.title)
          ? true
          : !products.length ? [{ length: false }] : products.map(p => [{ data: false }])
      }
    }
  }
}
